import {
  ActionIcon,
  Box,
  Group,
  Loader,
  Menu,
  Paper,
  Text,
  Badge,
  SimpleGrid,
} from '@mantine/core';
import { IconDots, IconEdit, IconTrash } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import type { PhysicalGrowthData } from '@/components/inputs/PhysicalGrowthInput';
import { LabeledField } from '@/components/LabeledField';

export type PhysicalGrowthAction = 'edit' | 'remove';

export interface PhysicalGrowthListItemProps {
  data: PhysicalGrowthData;
  onAction: (action: PhysicalGrowthAction) => void;
  isDeleting?: boolean;
  readonly?: boolean;
}

export const PhysicalGrowthListItem = ({
  data,
  onAction,
  isDeleting = false,
  readonly = false,
}: PhysicalGrowthListItemProps) => {
  const { t } = useTranslation(['patient-pages']);

  // Format the status value
  const formatStatus = (): string | null => {
    if (!data.value) {
      return null;
    }

    return data.value === 'within-normal-limits'
      ? t('physicalGrowth.withinNormalLimits', {
          ns: 'patient-pages',
          defaultValue: 'Within normal limits',
        })
      : t('physicalGrowth.pathological', {
          ns: 'patient-pages',
          defaultValue: 'Pathological',
        });
  };

  // Check if we have any measurements
  const hasMeasurements = data.weight || data.height || data.headCircumference;

  const hasActions = !isDeleting && !readonly;

  const statusText = formatStatus();

  return (
    <Paper p="sm" withBorder radius="md" h="100%">
      <Box flex={1}>
        {/* Header with status badge */}
        <Group justify="space-between" mb="xs">
          <Text fw={600} size="sm">
            {t('physicalGrowth.measurement', {
              ns: 'patient-pages',
              defaultValue: 'Physical Growth Measurement',
            })}
          </Text>

          <Group>
            {/* Status */}
            {statusText && (
              <Badge
                size="sm"
                variant="light"
                color={data.value === 'pathological' ? 'red' : 'green'}
              >
                {statusText}
              </Badge>
            )}
            {/* Actions menu or loading indicator */}
            {isDeleting ? (
              <Loader size="sm" />
            ) : (
              hasActions && (
                <Menu shadow="md" width={160} position="bottom-end">
                  <Menu.Target>
                    <ActionIcon variant="subtle" color="gray" size="sm">
                      <IconDots size={16} />
                    </ActionIcon>
                  </Menu.Target>

                  <Menu.Dropdown>
                    <Menu.Item
                      leftSection={<IconEdit size={14} />}
                      onClick={() => onAction('edit')}
                    >
                      {t('edit', {
                        ns: 'common',
                        defaultValue: 'Edit',
                      })}
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconTrash size={14} />}
                      onClick={() => onAction('remove')}
                      color="red"
                    >
                      {t('delete', {
                        ns: 'common',
                        defaultValue: 'Delete',
                      })}
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              )
            )}
          </Group>
        </Group>

        {/* Measurements */}
        {hasMeasurements && (
          <SimpleGrid cols={2} spacing="xs" verticalSpacing={4}>
            {data.weight && (
              <LabeledField
                label={t('examination.content.vitals.weight', {
                  ns: 'patient-pages',
                  defaultValue: 'Weight',
                })}
                value={`${data.weight} kg`}
              />
            )}
            {data.height && (
              <LabeledField
                label={t('examination.content.vitals.height', {
                  ns: 'patient-pages',
                  defaultValue: 'Height',
                })}
                value={`${data.height} cm`}
              />
            )}
            {data.headCircumference && (
              <LabeledField
                label={t('examination.content.vitals.headCircumference', {
                  ns: 'patient-pages',
                  defaultValue: 'Head Circumference',
                })}
                value={`${data.headCircumference} cm`}
              />
            )}
          </SimpleGrid>
        )}

        {/* Comments */}
        {data.comments && (
          <Box mt={4}>
            <LabeledField
              label={t('physicalGrowth.comments', {
                ns: 'patient-pages',
                defaultValue: 'Comments',
              })}
              value={data.comments}
            />
          </Box>
        )}
      </Box>
    </Paper>
  );
};
